// Pointer gestures on the 2D view and the step maths of the precision controls (spec §6). PURE:
// main.js feeds DOM events in and acts on what comes back.
//
//   tap  — the pointer moved ≤ DRAG_PX: select / cycle / deselect (src/editor/hit.js)
//   drag — touch & pen are TRACKPAD-STYLE: with an element selected, a drag that starts ANYWHERE
//          moves it by the finger's delta, so the finger never covers what it moves. A mouse is
//          precise and sees a cursor: it drags the element it pressed on, directly.
// No pinch, no twist (they fight browser zoom and LINE's in-app browser).
export const DRAG_PX = 6;
export const NUDGE_MM = 0.25, NUDGE_BIG_MM = 1;
export const ROT_STEP = 15, ROT_FINE = 1;
export const HOLD_MS = 400, REPEAT_MS = 80;

// start: { x, y (client px), pointerType, hits: ids under the pointer (topmost first),
//          selected: id | null, canDrag: boolean (advanced mode) }
export function startGesture({ x, y, pointerType, hits, selected, canDrag }) {
  let target = null;
  if (canDrag) {
    if (pointerType === 'mouse') target = hits.includes(selected) ? selected : (hits[0] ?? null);
    else target = selected;
  }
  return { x0: x, y0: y, hits, selected, target, dragging: false, dx: 0, dy: 0 };
}

// → the gesture with the pointer at (x, y); `dragging` latches once the pointer has moved > DRAG_PX.
export function moveGesture(g, { x, y }) {
  const dx = x - g.x0, dy = y - g.y0;
  return { ...g, dx, dy, dragging: g.dragging || (g.target !== null && Math.hypot(dx, dy) > DRAG_PX) };
}

// Screen pixels → model millimetres. The screen's y grows DOWNWARD, the model's UPWARD.
export const pxToModelDelta = (dxPx, dyPx, mmPerPx) => [dxPx * mmPerPx, -dyPx * mmPerPx];

// Where the dragged anchor is, given where it started.
export function dragPosition(g, start, mmPerPx) {
  const [dx, dy] = pxToModelDelta(g.dx, g.dy, mmPerPx);
  return { x: start.x + dx, y: start.y + dy };
}

// Arrow keys and nudge buttons. 'up' is +y (the model's y is up).
const DIR = { left: [-1, 0], right: [1, 0], up: [0, 1], down: [0, -1] };
export function nudge(pos, dir, step = NUDGE_MM) {
  const d = DIR[dir];
  if (!d) throw new Error('unknown direction: ' + dir);
  const r = (v) => Math.round(v * 100) / 100;
  return { x: r(pos.x + d[0] * step), y: r(pos.y + d[1] * step) };
}

// Rotate buttons. dir: 'ccw' (⟲, positive degrees — the model rotates counter-clockwise) or 'cw' (⟳).
// Coarse: to the NEXT multiple of 15° in that direction. Fine (long-press): 1°.
export function rotateStep(rot, dir, fine = false) {
  const sign = dir === 'ccw' ? 1 : dir === 'cw' ? -1 : null;
  if (sign === null) throw new Error('unknown rotation direction: ' + dir);
  if (fine) return rot + sign * ROT_FINE;
  const k = rot / ROT_STEP;
  const next = sign > 0 ? Math.floor(k + 1e-9) + 1 : Math.ceil(k - 1e-9) - 1;
  return next * ROT_STEP;
}

// The on-screen keyboard is open when the visual viewport is much shorter than the layout viewport.
export const keyboardOpen = (visualHeight, innerHeight) => innerHeight - visualHeight > 150;
